import NiceModal, { useModal } from '@ebay/nice-modal-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import {
  addendumItems,
  AddendumStatus,
  DocType,
  uploads,
  UploadsType,
} from '@prisma/client';
import addendumsService from '../../api/services/addendums';
import { addDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import DateTimeSelector from '../DateTimeSelector/DateTimeSelector';
import { useAppDispatch } from '../../store/store';
import { fetchStatus } from '../../store/slices/status';
import {
  fetchTransactions,
  transactionsWithUserAndOffer,
} from '../../store/slices/transactions';
import { useAuth } from '../../contexts/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import FileUpload from '../FileUpload/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import uploadsService from '../../api/services/uploads';

interface Props {
  transaction: transactionsWithUserAndOffer;
}

const RepairRequestSubmitModal = NiceModal.create<Props>(({ transaction }) => {
  const { listingId, addendum, id: transactionId } = transaction;
  const modal = useModal();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const initialDate = addDays(new Date(), 3);
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  const [_dateError, setDateError] = useState<string | null>(null);
  const [addendumItems, setAddendumItems] = useState<addendumItems[]>(
    addendum?.addendumItems || []
  );
  const [newItemText, setNewItemText] = useState('');
  const [uploads, setUploads] = useState<uploads[]>([]);

  const handleDateChange = (newDate: Date | null) => {
    setSelectedDate(newDate);
    setDateError(null);
  };

  const handleItemDelete = (id: string) => {
    setAddendumItems((items) => items.filter((item) => item.id !== id));
  };

  const handleAddItem = () => {
    if (!newItemText.trim()) return;

    const newItem = {
      id: uuidv4(),
      item: newItemText.trim(),
      completed: false,
    };

    setAddendumItems([...addendumItems, newItem as addendumItems]);
    setNewItemText('');
  };

  const findAddendumUpload = () => {
    let addendumUpload;
    if (uploads.length > 1) {
      addendumUpload = uploads.find(
        (upload) => upload.docType === DocType.INSPECTION_ADDENDUM
      );
      if (!addendumUpload) {
        addendumUpload = uploads[0];
      }
    } else {
      addendumUpload = uploads[0];
    }
    return addendumUpload;
  };

  const connectUploadsToAddendum = async (addendumId: string) => {
    if (uploads.length === 0) {
      return;
    }

    const addendumUpload = findAddendumUpload();
    await uploadsService.patch(addendumUpload.id, {
      addendum: {
        connect: { id: addendumId },
      },
      docType: DocType.INSPECTION_ADDENDUM,
    });
  };

  const createNewAddendum = async () => {
    if (uploads.length === 0) {
      enqueueSnackbar('No upload found', {
        variant: 'error',
      });
      return;
    }

    const addendumUpload = findAddendumUpload();
    try {
      const response = await addendumsService.create({
        responseDateTime: selectedDate!,
        status: AddendumStatus.PENDING,
        addendumItems: {
          create: addendumItems.map((item) => ({
            id: item.id,
            item: item.item,
            completed: item.completed,
          })),
        },
        transaction: {
          connect: {
            id: transactionId,
          },
        },
        terms: 'Repair Request',
        upload: {
          connect: {
            id: addendumUpload.id,
          },
        },
      });

      await connectUploadsToAddendum(response.id);
      return response;
    } catch (error) {
      throw new Error('Failed to create addendum');
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate) {
      setDateError('Please select a response date and time');
      return;
    }

    if (selectedDate < new Date()) {
      setDateError('Response date cannot be in the past');
      return;
    }

    try {
      if (!addendum) {
        // Create new addendum if one doesn't exist
        await createNewAddendum();
      } else {
        // Existing update logic
        const originalItemIds =
          addendum.addendumItems?.map((item) => item.id) || [];
        const currentItemIds = addendumItems.map((item) => item.id);
        const deletedItemIds = originalItemIds.filter(
          (id) => !currentItemIds.includes(id)
        );

        await addendumsService.put(addendum.id, {
          responseDateTime: selectedDate,
          addendumItems: {
            upsert: addendumItems.map((item) => ({
              where: { id: item.id },
              update: {
                item: item.item,
                completed: item.completed,
              },
              create: {
                id: item.id,
                item: item.item,
                completed: item.completed,
              },
            })),
            deleteMany:
              deletedItemIds.length > 0
                ? deletedItemIds.map((id) => ({ id }))
                : undefined,
          },
          status: AddendumStatus.PENDING,
        });
      }

      if (addendum) {
        await connectUploadsToAddendum(addendum.id);
      }

      dispatch(fetchTransactions());
      dispatch(fetchStatus(listingId));
      modal.remove();
    } catch (error) {
      enqueueSnackbar('Failed to submit repair request', {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog open={modal.visible} onClose={() => modal.remove()}>
      <DialogTitle>Submit Repair Requests</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Typography variant="body1">
            Please select the date and time by which a response to the
            inspection addendum is required.
          </Typography>
          <DateTimeSelector
            value={selectedDate}
            onChange={handleDateChange}
            showLabel={false}
            minDate={new Date()}
            label="Response Date"
            fullDayTimeOptions={true}
          />
          <Typography variant="body1">
            Please upload a repair request document or addendum below.
          </Typography>
          {currentUser?.id && (
            <FileUpload
              listingId={listingId}
              userId={currentUser?.id}
              docType={UploadsType.TRANSACTION_DOC}
              analyze={true}
              maxFiles={2}
              onUploadResponse={(e) => {
                const res = JSON.parse(e);
                if (res.length > 0) {
                  const analyzeRes = res[0]?.analyzeResult;
                  const terms = analyzeRes?.terms;
                  if (terms?.length > 0) {
                    setAddendumItems(
                      terms.map((term: string) => ({
                        id: uuidv4(),
                        item: term,
                        completed: false,
                      }))
                    );
                  }
                  const upload = res[0].uploadResult as uploads;
                  setUploads([...uploads, upload]);
                }
              }}
            />
          )}
        </Stack>

        {uploads.length > 0 && (
          <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
            <Stack direction="column" spacing={0}>
              <Typography variant="subtitle1" fontWeight={600}>
                Repair Request Items
              </Typography>
              <Typography variant="body2" color="text.secondary">
                These are the items that will be included in the repair request.
                Add any additional items below.
              </Typography>
            </Stack>
            {addendumItems.map((item) => (
              <Stack
                key={item.id}
                direction="row"
                spacing={2}
                alignItems="center"
              >
                <Typography sx={{ flexGrow: 1 }}>{item.item}</Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleItemDelete(item.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}

            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                size="small"
                placeholder="Add new repair request item"
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddItem();
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleAddItem}
                disabled={!newItemText.trim()}
              >
                <AddIcon />
              </IconButton>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => modal.remove()} color="error">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={addendumItems.length === 0}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default RepairRequestSubmitModal;
